/**
 * BOQ Dialog Components - Extracted from BOQWorksheets.js for maintainability
 * Each dialog receives only the props it needs from the parent component.
 * All state management remains in the parent (BOQWorksheets.js).
 */
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, Trash2, Link2Off, Unlock, Share2, Users, Link, Copy, ExternalLink, Clock, CalendarClock, CheckCircle, X, ClipboardCheck, ClipboardList } from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';

// ─── Delete BOQ Dialog ───
export function DeleteBOQDialog({ open, onOpenChange, selectedWorksheet, deleteReason, setDeleteReason, onDelete }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-stone-900">
            <AlertTriangle className="text-red-500" size={20} />
            Delete BOQ Worksheet
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p>Are you sure you want to delete <strong>{selectedWorksheet?.worksheet_number}</strong>?</p>
              <div>
                <Label>Reason for deletion</Label>
                <Textarea value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} placeholder="Optional: Enter reason..." className="mt-1" />
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── Delete Item Dialog ───
export function DeleteItemDialog({ open, onOpenChange, itemToDelete, deleteAction, setDeleteAction, onConfirm }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-stone-900">
            <Trash2 className="text-red-500" size={20} />Remove Line Item
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p>Remove &quot;{itemToDelete?.item?.task_title}&quot; from this BOQ?</p>
              <div className="space-y-2">
                <Label className="text-stone-900 font-medium">What should happen to the linked task?</Label>
                <div className="space-y-2">
                  <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-stone-50">
                    <input type="radio" name="deleteAction" value="unlink" checked={deleteAction === 'unlink'} onChange={() => setDeleteAction('unlink')} className="mt-1" />
                    <div>
                      <div className="flex items-center gap-2 font-medium text-stone-900"><Link2Off size={16} className="text-blue-500" />Unlink Task</div>
                      <p className="text-sm text-stone-500">Remove from BOQ but keep the task</p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-red-50">
                    <input type="radio" name="deleteAction" value="delete" checked={deleteAction === 'delete'} onChange={() => setDeleteAction('delete')} className="mt-1" />
                    <div>
                      <div className="flex items-center gap-2 font-medium text-stone-900"><Trash2 size={16} className="text-red-500" />Delete Task</div>
                      <p className="text-sm text-stone-500">Remove from BOQ AND delete the task</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className={deleteAction === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}>
            {deleteAction === 'delete' ? 'Delete Task' : 'Unlink Task'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── Unlock BOQ Dialog ───
export function UnlockBOQDialog({ open, onOpenChange, selectedWorksheet, unlockReason, setUnlockReason, onUnlock, onCancel }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-stone-900">
            <Unlock className="text-stone-600" size={20} />Unlock BOQ Worksheet
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p>You are about to unlock <strong>{selectedWorksheet?.worksheet_number}</strong>.
                {selectedWorksheet?.linked_quotation_number && (
                  <span className="text-stone-600 block mt-1">This BOQ is linked to quotation: {selectedWorksheet?.linked_quotation_number}</span>
                )}
              </p>
              <div className="p-3 bg-stone-50 border border-stone-200 rounded-lg text-sm text-stone-700">
                <strong>Warning:</strong> Changes to this BOQ will not automatically update the existing quotation. You may need to create a revised quotation after editing.
              </div>
              <div>
                <Label className="text-stone-900">Reason for unlocking *</Label>
                <Textarea value={unlockReason} onChange={(e) => setUnlockReason(e.target.value)} placeholder="Enter a detailed reason (minimum 10 characters)..." className="mt-1" data-testid="unlock-reason-input" />
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onUnlock} className="bg-stone-800 hover:bg-stone-900" data-testid="confirm-unlock-btn">
            <Unlock size={14} className="mr-1" />Unlock BOQ
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── Publish Client Dialog ───
export function PublishClientDialog({ open, onOpenChange, publishWorksheet, validityDate, setValidityDate, generatePublicLink, setGeneratePublicLink, publishing, onPublish }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Share2 className="text-blue-600" size={20} />Publish BOQ to Client</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800"><strong>Publishing:</strong> {publishWorksheet?.worksheet_number}</div>
          <div className="space-y-2">
            <Label htmlFor="validity-date" className="flex items-center gap-2"><CalendarClock size={16} className="text-stone-600" />Validity Until <span className="text-red-500">*</span></Label>
            {/* iter-313e: quick-pick chips for common validity windows */}
            <div className="flex flex-wrap gap-1.5" data-testid="validity-quick-picks">
              {[3, 7, 14, 20, 30, 60, 90].map((days) => {
                const target = new Date(Date.now() + days * 86400000).toISOString().split('T')[0];
                const isActive = validityDate === target;
                return (
                  <button
                    key={days}
                    type="button"
                    onClick={() => setValidityDate(target)}
                    className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                      isActive
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-stone-700 border-stone-200 hover:bg-blue-50 hover:border-blue-300'
                    }`}
                    data-testid={`validity-preset-${days}d`}
                  >
                    {days}d
                  </button>
                );
              })}
            </div>
            <Input id="validity-date" type="date" value={validityDate} onChange={(e) => setValidityDate(e.target.value)} min={new Date().toISOString().split('T')[0]} required />
            <p className="text-xs text-stone-500">Client must approve before this date. BOQ will auto-expire after this date.</p>
          </div>
          <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg border" onClick={(e) => e.stopPropagation()}>
            <Checkbox id="generate-public-link" checked={generatePublicLink} onCheckedChange={(checked) => setGeneratePublicLink(checked === true)} className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="generate-public-link" className="flex items-center gap-2 cursor-pointer"><Link size={14} className="text-purple-600" />Generate Temporary Public Link</Label>
              <p className="text-xs text-stone-500 mt-1">Create a shareable link that works without login. Link expires on validity date.</p>
            </div>
          </div>
          <div className="p-3 bg-stone-50 border border-stone-200 rounded-lg text-sm text-stone-700">
            <strong>Commercial Discipline:</strong>
            <ul className="mt-1 text-xs space-y-1"><li>BOQ will auto-expire after validity date</li><li>Client cannot approve after expiry</li><li>Creates urgency for timely decisions</li></ul>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onPublish} disabled={publishing || !validityDate} className="bg-blue-600 hover:bg-blue-700">
            {publishing ? 'Publishing...' : <><Share2 size={16} className="mr-2" />Publish to Client</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Publish Vendor Dialog ───
export function PublishVendorDialog({ open, onOpenChange, publishVendorWorksheet, generateVendorPublicLink, setGenerateVendorPublicLink, publishingVendor, onPublish }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="text-purple-600" size={20} />Publish to Vendors</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
            <p className="text-sm text-purple-800"><strong>Vendor Visibility Rules:</strong></p>
            <ul className="text-xs text-purple-700 mt-1 space-y-1"><li>Each vendor sees only their assigned tasks</li><li>Vendor rates only (no client/admin rates)</li><li>Uses same validity date as client publish</li></ul>
          </div>
          {publishVendorWorksheet?.validity_date && (
            <div className="p-3 bg-stone-50 rounded-lg"><p className="text-sm text-stone-700"><Clock size={14} className="inline mr-2 text-stone-600" /><strong>Validity:</strong> {formatDate(publishVendorWorksheet.validity_date, { longMonth: true })}</p></div>
          )}
          {!publishVendorWorksheet?.validity_date && (
            <div className="p-3 bg-stone-50 border border-stone-200 rounded-lg text-sm text-stone-700"><AlertTriangle size={14} className="inline mr-2" />Please publish to client first to set validity date.</div>
          )}
          <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg border" onClick={(e) => e.stopPropagation()}>
            <Checkbox id="generate-vendor-public-link" checked={generateVendorPublicLink} onCheckedChange={(checked) => setGenerateVendorPublicLink(checked === true)} className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="generate-vendor-public-link" className="flex items-center gap-2 cursor-pointer"><Link size={14} className="text-purple-600" />Generate Vendor Public Link</Label>
              <p className="text-xs text-stone-500 mt-1">Create a shareable link for vendors. Shows vendor rates only.</p>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onPublish} disabled={publishingVendor || (!publishVendorWorksheet?.validity_date)} className="bg-purple-600 hover:bg-purple-700">
            {publishingVendor ? 'Publishing...' : <><Users size={16} className="mr-2" />Publish to Vendors</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Public Link Dialog ───
export function PublicLinkDialog({ open, onOpenChange, currentPublicLink, onCopyLink }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link className={currentPublicLink?.type === 'vendor' ? 'text-purple-600' : 'text-blue-600'} size={20} />
            {currentPublicLink?.type === 'vendor' ? 'Vendor' : 'Client'} Public BOQ Link
          </DialogTitle>
        </DialogHeader>
        {currentPublicLink && (
          <div className="space-y-4 py-4">
            <div className={`p-3 border rounded-lg ${currentPublicLink.type === 'vendor' ? 'bg-purple-50 border-purple-200' : 'bg-green-50 border-green-200'}`}>
              <p className={`text-sm font-medium mb-2 ${currentPublicLink.type === 'vendor' ? 'text-purple-800' : 'text-green-800'}`}>
                {currentPublicLink.type === 'vendor' ? 'Vendor' : 'Client'} link is active
              </p>
              <div className="flex items-center gap-2">
                <Input value={currentPublicLink.url} readOnly className="text-sm font-mono bg-white" />
                <Button size="sm" variant="outline" onClick={() => onCopyLink(currentPublicLink.url)}><Copy size={14} /></Button>
                <Button size="sm" variant="outline" onClick={() => window.open(currentPublicLink.url, '_blank')}><ExternalLink size={14} /></Button>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-stone-600">
              <Clock size={14} className="text-stone-600" />
              <span>Expires: {currentPublicLink.expiresAt ? formatDate(currentPublicLink.expiresAt, { shortMonth: true, includeTime: true }) : 'Not set'}</span>
            </div>
            <div className="p-3 bg-stone-50 border rounded-lg text-sm">
              <p className="font-medium text-stone-700 mb-2">Share this link with your {currentPublicLink.type === 'vendor' ? 'vendor' : 'client'}:</p>
              <ul className="text-xs text-stone-600 space-y-1">
                <li>No login required to view</li>
                <li>Shows {currentPublicLink.type === 'vendor' ? 'vendor rates (markdown applied)' : 'client-facing prices (markup applied)'}</li>
                <li>Auto-deactivates after validity date</li>
                <li>Can be revoked anytime</li>
              </ul>
            </div>
          </div>
        )}
        <div className="flex justify-end"><Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button></div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Confirm BOQ Dialog ───
export function ConfirmBOQDialog({ open, onOpenChange, confirmWorksheet, confirmStatus, setConfirmStatus, confirmNotes, setConfirmNotes, confirming, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck size={20} className="text-emerald-600" />
            {confirmStatus === 'confirmed' ? 'Confirm BOQ' : 'Update BOQ Status'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <p className="text-sm text-stone-600">
            {confirmStatus === 'confirmed' ? `Marking this BOQ as "Confirmed" will include it in the Dashboard Fund Status tracking.` : `Update the confirmation status for BOQ ${confirmWorksheet?.worksheet_number}.`}
          </p>
          <div>
            <Label>Status</Label>
            <Select value={confirmStatus} onValueChange={setConfirmStatus}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="confirmed"><div className="flex items-center gap-2"><CheckCircle size={14} className="text-emerald-600" />Confirmed</div></SelectItem>
                <SelectItem value="rejected"><div className="flex items-center gap-2"><X size={14} className="text-red-600" />Rejected</div></SelectItem>
                <SelectItem value="pending"><div className="flex items-center gap-2"><Clock size={14} className="text-orange-600" />Pending</div></SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea value={confirmNotes} onChange={(e) => setConfirmNotes(e.target.value)} placeholder="Add any notes about this confirmation..." className="mt-1" />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onConfirm} disabled={confirming} className={confirmStatus === 'confirmed' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}>
            {confirming ? 'Updating...' : confirmStatus === 'confirmed' ? 'Confirm BOQ' : 'Update Status'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
